import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen, CalendarCheck } from "lucide-react";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEOHead from "@/components/SEOHead";
import { GUIDES, GUIDES_HUB, formatGuideDate } from "@/data/guidesContent";
import { buildGuideListSchema } from "@/lib/schema/guidePageSchema";
import { fadeUp, stagger } from "@/utils/motionVariants";

/**
 * /guides — index of the permanent comparison guides. The single "Guides"
 * entry in the nav/footer points here; individual guides are never added
 * to the menu. New guides appear automatically from data/guidesContent.js.
 */
export default function GuidesHubPage() {
  return (
    <div data-testid="guides-hub-page">
      <SEOHead
        title={GUIDES_HUB.seoTitle}
        description={GUIDES_HUB.metaDescription}
        path={GUIDES_HUB.path}
        schema={buildGuideListSchema(GUIDES_HUB, GUIDES)}
      />

      <section className="hero-gradient py-14 md:py-20" data-testid="guides-hero">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <Breadcrumbs className="mb-5" />
            <motion.p
              variants={fadeUp}
              className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.25em] text-brand-aqua-dark mb-4"
            >
              <BookOpen size={12} aria-hidden="true" />
              {GUIDES_HUB.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-heading text-4xl sm:text-5xl font-bold text-brand-black tracking-tight leading-tight mb-5"
            >
              {GUIDES_HUB.h1}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lg text-brand-grey max-w-3xl leading-relaxed">
              {GUIDES_HUB.intro}
            </motion.p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-16 bg-white" data-testid="guides-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ul className="grid grid-cols-1 md:grid-cols-2 gap-5 list-none p-0 m-0">
            {GUIDES.map((g) => (
              <li key={g.slug}>
                <Link
                  to={g.path}
                  className="group h-full flex flex-col bg-gray-50 border border-brand-light-grey rounded-2xl p-6 hover:border-brand-aqua/50 hover:bg-white hover:shadow-md transition-all"
                  data-testid={`guides-card-${g.slug}`}
                >
                  <span className="text-xs font-bold uppercase tracking-[0.2em] text-brand-aqua-deep mb-2">{g.eyebrow}</span>
                  <span className="font-heading text-xl font-bold text-brand-black mb-2">{g.h1}</span>
                  <span className="block text-sm text-brand-grey-dark leading-relaxed mb-4 flex-1">{g.intro[0]}</span>
                  {g.reviewed && (
                    <span className="inline-flex items-center gap-1.5 text-xs text-brand-grey mb-4">
                      <CalendarCheck size={12} aria-hidden="true" />
                      {`Last reviewed ${formatGuideDate(g.reviewed)}`}
                    </span>
                  )}
                  <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand-aqua-deep group-hover:text-brand-black transition-colors">
                    {GUIDES_HUB.cardCta}
                    <ArrowRight size={14} aria-hidden="true" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
